import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, Search, Filter, Calendar, AlertCircle, FolderOpen } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { formatWithUTC } from '@/lib/datetime'

interface Audit {
  id: string
  title: string
  description: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'initial_review' | 'detailed_analysis' | 'error'
  created_at: string
  updated_at: string
  user_id: string
}

interface Organization {
  id: string
  name: string
  login?: string
  avatar_url?: string
}

interface UserProfile {
  id: string
  full_name?: string
  github_username?: string
  avatar_url?: string
}

interface CasesPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const statusColors = {
  pending: 'bg-warning/10 text-warning border-warning/20',
  processing: 'bg-primary/10 text-primary border-primary/20', 
  completed: 'bg-success/10 text-success border-success/20',
  failed: 'bg-destructive/10 text-destructive border-destructive/20'
}

const getStatusLabels = (t: any) => ({
  pending: t('cases.filter.pending'),
  processing: t('cases.filter.processing'),
  completed: t('cases.filter.completed'),
  failed: t('cases.filter.failed')
})

export function CasesPage({ organization, profile }: CasesPageProps) {
  const { t } = useTranslation()
  const [cases, setCases] = useState<Audit[]>([])
  const [filteredCases, setFilteredCases] = useState<Audit[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const statusLabels = getStatusLabels(t)

  useEffect(() => {
    fetchCases()
  }, [])

  useEffect(() => {
    filterCases()
  }, [cases, searchQuery, statusFilter])

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('audits')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setCases(data || [])
    } catch (error) {
      console.error('Error fetching cases:', error)
      toast({
        title: t("cases.toast.error"),
        description: t("cases.toast.errorDesc"),
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const filterCases = () => {
    let filtered = cases

    if (searchQuery) {
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter)
    }

    setFilteredCases(filtered)
  }

  const createNewCase = () => {
    navigate('/cases/new')
  }

  const handleCaseClick = (caseId: string) => {
    navigate(`/cases/${caseId}`)
  }

  // Removed - using formatWithUTC from @/lib/datetime

  const getStatusStats = () => {
    const stats = cases.reduce((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    return stats
  }

  const stats = getStatusStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('cases.title.page')}</h1>
          <p className="text-muted-foreground">{t('cases.subtitle')}</p>
        </div>
        <Button onClick={createNewCase} className="gap-2">
          <Plus className="h-4 w-4" />
          {t('cases.newCase')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('cases.stats.total')}</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{cases.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('cases.stats.active')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{(stats.pending || 0) + (stats.processing || 0)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('cases.stats.completed')}</CardTitle>
            <Calendar className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{stats.completed || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('cases.stats.failed')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats.failed || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('cases.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t('cases.filter.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('cases.filter.all')}</SelectItem>
                <SelectItem value="pending">{t('cases.filter.pending')}</SelectItem>
                <SelectItem value="processing">{t('cases.filter.processing')}</SelectItem>
                <SelectItem value="completed">{t('cases.filter.completed')}</SelectItem>
                <SelectItem value="failed">{t('cases.filter.failed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('cases.table.title')}</TableHead>
                <TableHead>{t('cases.table.status')}</TableHead>
                <TableHead>{t('cases.table.created')}</TableHead>
                <TableHead>{t('cases.table.updated')}</TableHead>
                <TableHead className="text-right">{t('cases.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {cases.length === 0 ? t('cases.empty.none') : t('cases.empty.filtered')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCases.map((auditCase) => (
                  <TableRow 
                    key={auditCase.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleCaseClick(auditCase.id)}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{auditCase.title}</div>
                        {auditCase.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {auditCase.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[auditCase.status]}>
                        {statusLabels[auditCase.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatWithUTC(auditCase.created_at)}</TableCell>
                    <TableCell>{formatWithUTC(auditCase.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        {t('cases.table.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}